import React, { useState } from 'react';
import { DocumentText, Trash, ShieldTick } from 'iconsax-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/use-toast';
import { Alert, AlertDescription } from '../../components/ui/alert';
import demoService from '../../services/demoService';

const DemoManagement = () => {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  const handleFeedDemo = async () => {
    try {
      setLoading(true);
      const res = await demoService.feedDemoData();
      if (res.success) {
        toast({
          title: 'Success',
          description: res.message || 'Demo data generated successfully.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to generate demo data.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDemo = async () => {
    if (!window.confirm("Are you sure you want to delete ALL demo data? This action cannot be undone.")) {
      return;
    }
    
    try {
      setDeleting(true);
      const res = await demoService.deleteDemoData();
      if (res.success) {
        toast({
          title: 'Success',
          description: res.message || 'All demo data deleted successfully.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete demo data.',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in-50 duration-500">
      
      <div className="flex flex-col gap-1 pb-4 border-b border-border/40">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">
          Demo Management
        </h1>
        <p className="text-sm text-muted-foreground">
          Generate or remove realistic dummy data for testing and demonstration purposes.
        </p>
      </div>

      <Alert className="bg-blue-50/50 text-blue-800 border-blue-200">
        <ShieldTick size={20} className="text-blue-600 -mt-0.5" />
        <h5 className="font-medium leading-none tracking-tight mb-1">Admin Feature</h5>
        <AlertDescription>
          This section allows you to quickly populate the platform with comprehensive mock data, including organizations, caregivers, patients, care plans, and historical shifts spanning the last two months.
        </AlertDescription>
      </Alert>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Feed Data Card */}
        <Card className="border-border/50 shadow-sm transition-all hover:shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
              <DocumentText size={20} className="text-emerald-600" />
              Feed Demo Data
            </CardTitle>
            <CardDescription>
              Generates a new Agency marked as Demo, complete with realistic users, active care plans, and 50 past completed shifts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-slate-50 rounded-md border border-slate-100 mb-2">
              <ul className="text-sm text-slate-600 space-y-2 list-disc list-inside">
                <li>1 New Demo Agency</li>
                <li>1 Agency Admin User (Password: Admin@123)</li>
                <li>5 Caregivers & 10 Patients</li>
                <li>10 Care Plans & 50 historical shifts</li>
              </ul>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleFeedDemo} 
              disabled={loading || deleting} 
              className="w-full bg-emerald-600 hover:bg-emerald-700 shadow-sm"
            >
              {loading ? 'Generating...' : 'Generate Demo Data'}
            </Button>
          </CardFooter>
        </Card>

        {/* Delete Data Card */}
        <Card className="border-border/50 shadow-sm transition-all hover:shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
              <Trash size={20} className="text-destructive" />
              Delete Demo Data
            </CardTitle>
            <CardDescription>
              Completely wipes all data associated with generated demo agencies. Use this to clean up the environment after testing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 bg-red-50/50 rounded-md border border-red-100 mb-2">
              <p className="text-sm text-red-800 font-medium mb-1">Warning: Destructive Action</p>
              <p className="text-xs text-red-600/80">
                This will find any agency flagged as 'demo' and perform a cascading deletion of all associated patients, shifts, and users.
              </p>
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleDeleteDemo} 
              disabled={loading || deleting} 
              variant="destructive"
              className="w-full shadow-sm"
            >
              {deleting ? 'Deleting...' : 'Delete All Demo Data'}
            </Button>
          </CardFooter>
        </Card>
      </div>

    </div>
  );
};

export default DemoManagement;
